import React, { useRef, useEffect } from 'react';

interface FormPhoneInputProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  countryCode?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

const FormPhoneInput: React.FC<FormPhoneInputProps> = ({
  label,
  required = false,
  value,
  onChange,
  countryCode = '+91',
  placeholder = 'Enter',
  error,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const prefixRef = useRef<HTMLDivElement>(null);

  const textColor = '#E2E8F0';

  // Function to fix autofill styling
  const fixAutofillStyling = (element: HTMLInputElement) => {
    if (disabled) {
      return;
    }
    // Force text color to be light
    element.style.color = textColor;
    element.style.WebkitTextFillColor = textColor;
    const bgColor = 'var(--bg-input-default)';
    element.style.backgroundColor = bgColor;
    element.style.background = bgColor;
    element.style.WebkitBoxShadow = `0 0 0px 1000px ${bgColor} inset`;
    element.style.boxShadow = `inset 0px 2px 4px 0px rgba(0,0,0,0.06), 0 0 0 1000px ${bgColor} inset`;
  };

  // Monitor for autofill changes
  useEffect(() => {
    const input = inputRef.current;
    if (!input || disabled) {
      return;
    }

    // Function to check and fix autofill
    const checkAndFixAutofill = () => {
      if (input.matches(':-webkit-autofill')) {
        fixAutofillStyling(input);
      }
    };

    // Check immediately and on intervals
    checkAndFixAutofill();
    const interval = setInterval(checkAndFixAutofill, 100);

    // Also check on focus/blur
    const handleFocus = () => {
      setTimeout(checkAndFixAutofill, 0);
    };
    const handleBlur = () => {
      setTimeout(checkAndFixAutofill, 0);
    };

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);

    return () => {
      clearInterval(interval);
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, textColor]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
      }}
    >
      {/* Label */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          position: 'relative',
        }}
      >
        <label
          style={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--label-color-default)',
            lineHeight: '20px',
          }}
        >
          {label}
        </label>
        {required && (
          <span
            style={{
              color: '#dc2626',
              fontSize: '10px',
              position: 'absolute',
              left: '-8px',
              top: '2px',
              fontWeight: 600,
            }}
          >
            *
          </span>
        )}
      </div>

      {/* Phone Input with Country Code */}
      <div
        style={{
          display: 'flex',
          border: `1px solid ${error ? '#dc2626' : disabled ? 'var(--border-input-disabled)' : 'var(--border-input-default)'}`,
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        {/* Country Code Prefix */}
        <div
          ref={prefixRef}
          style={{
            backgroundColor: disabled ? 'var(--bg-input-disabled)' : 'var(--bg-input-default)',
            background: disabled ? 'var(--bg-input-disabled)' : 'var(--bg-input-default)',
            border: `1px solid ${disabled ? 'var(--border-input-disabled)' : 'var(--border-input-default)'}`,
            borderRight: 'none',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 12px',
            borderRadius: '4px 0 0 4px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#E2E8F0', // Use #E2E8F0 for better visibility (both enabled and disabled)
            flexShrink: 0,
          }}
        >
          {countryCode}
        </div>

        {/* Phone Number Input */}
        <input
          ref={inputRef}
          type="tel"
          value={value}
          disabled={disabled}
          autoComplete="off"
          maxLength={10}
          onChange={e => {
            if (!disabled) {
              // Only allow numbers and limit to 10 digits
              const numericValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
              onChange(numericValue);
              // Maintain text color while typing to prevent browser from applying black text
              fixAutofillStyling(e.currentTarget);
            }
          }}
          onKeyDown={e => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            // Prevent non-numeric keys except backspace, delete, tab, arrow keys
            const allowedKeys = [
              'Backspace',
              'Delete',
              'Tab',
              'ArrowLeft',
              'ArrowRight',
              'ArrowUp',
              'ArrowDown',
              'Home',
              'End',
            ];
            if (
              !allowedKeys.includes(e.key) &&
              !(e.ctrlKey || e.metaKey) && // Allow Ctrl/Cmd + A, C, V, etc.
              !(e.key >= '0' && e.key <= '9')
            ) {
              e.preventDefault();
            }
          }}
          onPaste={e => {
            if (!disabled) {
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text');
              const numericValue = pastedText.replace(/[^0-9]/g, '').slice(0, 10);
              onChange(numericValue);
            }
          }}
          placeholder={placeholder}
          style={
            {
              flex: 1,
              height: '36px',
              padding: '8px 12px',
              backgroundColor: disabled ? 'var(--bg-input-disabled)' : 'var(--bg-input-default)',
              background: disabled ? 'var(--bg-input-disabled)' : 'var(--bg-input-default)',
              border: `1px solid ${error ? '#dc2626' : disabled ? 'var(--border-input-disabled)' : 'var(--border-input-default)'}`,
              borderLeft: 'none',
              borderRadius: '0 6px 6px 0',
              fontSize: '14px',
              fontWeight: 400,
              color: '#E2E8F0', // Use #E2E8F0 for better visibility (both enabled and disabled)
              WebkitTextFillColor: '#E2E8F0', // Ensure text color is maintained
              outline: 'none',
              boxShadow: 'inset 0px 2px 4px 0px rgba(0,0,0,0.06)',
              cursor: disabled ? 'not-allowed' : 'text',
              pointerEvents: disabled ? 'none' : 'auto',
            } as React.CSSProperties
          }
          onFocus={e => {
            if (!disabled) {
              e.currentTarget.style.borderColor = 'var(--accent-primary)';
              e.currentTarget.parentElement!.style.borderColor = 'var(--accent-primary)';
              // Maintain text color on focus to prevent browser from applying black text
              fixAutofillStyling(e.currentTarget);
            }
          }}
          onAnimationStart={e => {
            // Browsers trigger an animation when autofill is applied
            if (e.animationName === 'onAutoFillStart' || e.animationName === 'autofill') {
              fixAutofillStyling(e.currentTarget);
            }
          }}
          onBlur={e => {
            if (disabled) {
              const borderColor = 'var(--border-input-disabled)';
              e.currentTarget.style.borderColor = borderColor;
              e.currentTarget.parentElement!.style.borderColor = borderColor;
              e.currentTarget.style.backgroundColor = 'var(--bg-input-disabled)';
              e.currentTarget.style.background = 'var(--bg-input-disabled)';
              e.currentTarget.style.color = '#E2E8F0'; // Use #E2E8F0 for better visibility
            } else {
              const borderColor = 'var(--border-input-default)';
              e.currentTarget.style.borderColor = borderColor;
              e.currentTarget.parentElement!.style.borderColor = borderColor;
              // Fix autofill styling on blur
              fixAutofillStyling(e.currentTarget);
            }
          }}
          onClick={
            disabled
              ? e => {
                  e.preventDefault();
                  e.stopPropagation();
                }
              : e => {
                  // Fix text color when clicking into input (even if not from autofill)
                  if (!disabled) {
                    fixAutofillStyling(e.currentTarget);
                  }
                }
          }
        />
      </div>

      {/* Error Message */}
      {error && (
        <span
          style={{
            fontSize: '12px',
            color: '#dc2626',
            marginTop: '4px',
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default FormPhoneInput;
